import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api, type Client, type Package } from '@/lib/api'
import { formatTime, parseTime } from './calendarHelpers'
import { PresetSelect } from './PresetSelect'
import { DatePicker } from '@/components/ui/DatePicker'
import { TimePicker } from '@/components/ui/TimePicker'

export interface ApptFormBody {
  // OPT-011：学员与课包均必选（后端 packageId 必填）
  clientId: number
  packageId: number
  title?: string
  scheduledDate: number
  startTime: string
  endTime: string
  location?: string
  notes?: string
}

/** 可绑定的课包：active 且剩余>0 且未过期（以当前时间判断，expiresAt 为秒级 epoch） */
export function isUsablePackage(p: Package, now = Date.now()): boolean {
  return p.status === 'active' && p.remainingSessions > 0 && (!p.expiresAt || p.expiresAt * 1000 > now)
}

export interface ApptFormFieldsProps {
  clients: Client[]
  defaultDate?: string
  defaultStartTime?: string
  defaultEndTime?: string
  onClose: () => void
  onSubmit: (body: ApptFormBody) => void
}

export interface ApptFormProps extends ApptFormFieldsProps {
  open: boolean
}

function addOneHour(time: string): string {
  return formatTime(parseTime(time) + 60)
}

/** 新建预约表单主体（无外壳）：桌面右侧面板与移动端 Dialog 共用 */
export function ApptFormFields({
  clients,
  defaultDate,
  defaultStartTime,
  defaultEndTime,
  onClose,
  onSubmit,
}: ApptFormFieldsProps) {
  const { t } = useTranslation()
  const [clientId, setClientId] = useState<number | undefined>(undefined)
  const [packageId, setPackageId] = useState<number | undefined>(undefined)
  const start = defaultStartTime || '10:00'
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0])
  const [startTime, setStartTime] = useState(start)
  const [endTime, setEndTime] = useState(defaultEndTime || addOneHour(start))
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')

  // 换学员后重新选课包（课包绑定到学员，旧选择必然失效）
  useEffect(() => {
    setPackageId(undefined)
  }, [clientId])

  // 选定学员后拉取其课包列表（后端按 clientId 过滤）
  const { data: pkgData, isLoading: pkgLoading } = useQuery({
    queryKey: ['packages', 'client', clientId],
    queryFn: () => api.getClientPackages(clientId!),
    enabled: clientId != null,
  })
  const usablePackages = useMemo(
    () => (pkgData?.items || []).filter((p) => isUsablePackage(p)),
    [pkgData],
  )
  // 学员已选且课包加载完仍无可选项 → 提示先加课并禁用提交
  const noUsablePackages = clientId != null && !pkgLoading && usablePackages.length === 0

  // Sync defaults when they change (e.g. opening form from different slots)
  const derivedDefaults = useMemo(
    () => ({
      date: defaultDate || new Date().toISOString().split('T')[0],
      startTime: defaultStartTime || '10:00',
      endTime: defaultEndTime || addOneHour(defaultStartTime || '10:00'),
    }),
    [defaultDate, defaultStartTime, defaultEndTime],
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!clientId || !packageId) return
        const scheduledDate = Math.floor(new Date(date).getTime() / 1000)
        onSubmit({
          clientId,
          packageId,
          scheduledDate,
          startTime,
          endTime,
          location: location || undefined,
          notes: notes || undefined,
        })
      }}
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="clientId">{t('schedule.form.client')}</Label>
          {/* 学员必选：占位选项禁用，未显式选择时 required 校验阻止提交 */}
          <select
            id="clientId"
            required
            value={clientId || ''}
            onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full border rounded px-2 py-1 bg-white"
          >
            <option value="" disabled>
              {t('schedule.form.selectClient')}
            </option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="packageId">{t('schedule.form.package')}</Label>
          {/* 课包必选：选项格式"课包名（剩余 N 课时）"；未选学员时无可选项 */}
          <select
            id="packageId"
            required
            value={packageId || ''}
            onChange={(e) => setPackageId(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full border rounded px-2 py-1 bg-white"
          >
            <option value="" disabled>
              {t('schedule.form.selectPackage')}
            </option>
            {usablePackages.map((p) => (
              <option key={p.id} value={p.id}>
                {t('schedule.form.packageOption', { name: p.name, count: p.remainingSessions })}
              </option>
            ))}
          </select>
          {noUsablePackages && (
            <p className="mt-1 text-sm text-red-600">{t('schedule.form.noUsablePackages')}</p>
          )}
        </div>
        <div>
          <Label htmlFor="date">{t('schedule.form.date')}</Label>
          <DatePicker
            id="date"
            value={date}
            onChange={(v) => {
              setDate(v)
              if (v === derivedDefaults.date) {
                setStartTime(derivedDefaults.startTime)
                setEndTime(derivedDefaults.endTime)
              }
            }}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="start">{t('schedule.form.start')}</Label>
            <TimePicker
              id="start"
              value={startTime}
              onChange={(v) => {
                setStartTime(v)
                setEndTime(addOneHour(v))
              }}
              required
            />
          </div>
          <div>
            <Label htmlFor="end">{t('schedule.form.end')}</Label>
            <TimePicker
              id="end"
              value={endTime}
              onChange={(v) => setEndTime(v)}
              required
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="location">{t('schedule.form.location')}</Label>
            <PresetSelect kind="location" onSelect={(text) => setLocation(text)} />
          </div>
          <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="notes">{t('common.notes')}</Label>
            <PresetSelect kind="note" onSelect={(text) => setNotes(text)} />
          </div>
          <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={noUsablePackages}>{t('common.save')}</Button>
      </div>
    </form>
  )
}

/** 移动端/旧入口：Dialog 包装（行为与 props 不变） */
export function ApptForm({ open, onClose, ...fieldsProps }: ApptFormProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('schedule.new')}</DialogTitle>
        </DialogHeader>
        <ApptFormFields {...fieldsProps} onClose={onClose} />
      </DialogContent>
    </Dialog>
  )
}
